import React from 'react';
import styled, { keyframes } from 'styled-components';

// ─── 3-Step Sequence: Step 1 Flip -> Step 2 Rotate -> Step 3 Flip ─────────────
const cubeStepSequence = keyframes`
  /* Step 1: 1st Flip (X axis 0deg -> 90deg) */
  0% {
    transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg);
  }
  28%, 33% {
    transform: rotateX(90deg) rotateY(0deg) rotateZ(0deg);
  }

  /* Step 2: 2nd Rotate (Y axis 0deg -> 90deg) */
  61%, 66% {
    transform: rotateX(90deg) rotateY(90deg) rotateZ(0deg);
  }

  /* Step 3: 3rd Flip (X axis 90deg -> 180deg) */
  94%, 100% {
    transform: rotateX(180deg) rotateY(90deg) rotateZ(0deg);
  }
`;

// ─── Color Shift on Every Step ───────────────────────────────────────────────
const faceColorShift = keyframes`
  0%, 28% {
    filter: hue-rotate(0deg);
  }
  33%, 61% {
    filter: hue-rotate(120deg);
  }
  66%, 94% {
    filter: hue-rotate(240deg);
  }
  100% {
    filter: hue-rotate(360deg);
  }
`;

// ─── Floor Shadow Pulse ───────────────────────────────────────────────────────
const shadowPulse = keyframes`
  0%, 33%, 66%, 100% {
    transform: scale(0.85);
    opacity: 0.3;
  }
  16%, 49%, 82% {
    transform: scale(1.15);
    opacity: 0.15;
  }
`;

// ─── Styled Components ───────────────────────────────────────────────────────
const Wrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ $size }) => Math.max(14, $size * 0.35)}px;
  user-select: none;
`;

const Scene = styled.div`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  perspective: ${({ $size }) => $size * 5}px;
  position: relative;
`;

const Cube = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: ${cubeStepSequence} 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
`;

// Clean 3D Face — NO face shadow box-shadow spread!
const Face = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: ${({ $size }) => Math.max(3, $size * 0.12)}px;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
  animation: ${faceColorShift} 2.4s ease-in-out infinite;

  /* Glass top specular highlight */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 100%);
    border-radius: ${({ $size }) => Math.max(2, $size * 0.1)}px ${({ $size }) => Math.max(2, $size * 0.1)}px 0 0;
    pointer-events: none;
  }
`;

// 6 Cube Faces with rich solid gradients
const FrontFace = styled(Face)`
  background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
  transform: rotateY(0deg) translateZ(${({ $half }) => $half}px);
`;

const BackFace = styled(Face)`
  background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
  transform: rotateY(180deg) translateZ(${({ $half }) => $half}px);
`;

const RightFace = styled(Face)`
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  transform: rotateY(90deg) translateZ(${({ $half }) => $half}px);
`;

const LeftFace = styled(Face)`
  background: linear-gradient(135deg, #ea580c 0%, #f97316 100%);
  transform: rotateY(-90deg) translateZ(${({ $half }) => $half}px);
`;

const TopFace = styled(Face)`
  background: linear-gradient(135deg, #db2777 0%, #ec4899 100%);
  transform: rotateX(90deg) translateZ(${({ $half }) => $half}px);
`;

const BottomFace = styled(Face)`
  background: linear-gradient(135deg, #0284c7 0%, #06b6d4 100%);
  transform: rotateX(-90deg) translateZ(${({ $half }) => $half}px);
`;

const Shadow = styled.div`
  width: ${({ $size }) => $size * 0.85}px;
  height: ${({ $size }) => Math.max(5, $size * 0.16)}px;
  background: radial-gradient(ellipse at center, rgba(124, 58, 237, 0.4) 0%, rgba(0, 0, 0, 0) 75%);
  border-radius: 50%;
  animation: ${shadowPulse} 2.4s ease-in-out infinite;
`;

const Text = styled.span`
  font-size: ${({ $size }) => Math.max(11, $size * 0.28)}px;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.3px;
`;

const OverlayContainer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
`;

/**
 * Cube3DLoader Component
 * Sharp 3D Cube with 3-Step Sequence: Step 1 Flip -> Step 2 Rotate -> Step 3 Flip
 *
 * @param {number} size - Cube size in pixels (default 42)
 * @param {string} text - Optional loading text below cube
 * @param {boolean} overlay - Whether to render full container overlay
 */
export function Cube3DLoader({ size = 42, text, overlay = false }) {
  const half = size / 2;

  const content = (
    <Wrapper $size={size}>
      <Scene $size={size}>
        <Cube>
          <FrontFace $size={size} $half={half} />
          <BackFace $size={size} $half={half} />
          <RightFace $size={size} $half={half} />
          <LeftFace $size={size} $half={half} />
          <TopFace $size={size} $half={half} />
          <BottomFace $size={size} $half={half} />
        </Cube>
      </Scene>
      <Shadow $size={size} />
      {text && <Text $size={size}>{text}</Text>}
    </Wrapper>
  );

  if (overlay) {
    return <OverlayContainer>{content}</OverlayContainer>;
  }

  return content;
}

export default Cube3DLoader;
